import React from 'react';
import { Button, Input, addToast } from '@heroui/react';
import { Search, Plane } from 'lucide-react';
import BaseApi from '../../../api/BaseApi.js';

const HISTORY_KEY = 'aircraftSearchHistory';
const HISTORY_LIMIT = 10;

/**
 * Aircraft model search component with results list and pagination.
 * Used in AddAircraftModal and ViewAircraftPage for selecting aircraft models.
 *
 * @param {Object} props
 * @param {Function} props.onSelect - Callback when model is selected: (model) => void
 * @param {boolean} [props.showHistory=true] - Show search history from localStorage
 * @param {string} [props.size='sm'] - Input size: 'sm' | 'md' | 'lg'
 * @param {string} [props.placeholder='Model name or manufacturer'] - Input placeholder
 * @param {number} [props.maxHeight=192] - Max height for results list in pixels (default 12rem = 192px)
 */
function AircraftModelSearch({
	onSelect,
	showHistory = true,
	size = 'sm',
	placeholder = 'Model name or manufacturer',
	maxHeight = 192
}) {
	const [query, setQuery] = React.useState('');
	const [results, setResults] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [page, setPage] = React.useState(1);
	const [hasMore, setHasMore] = React.useState(false);

	// Search history helpers
	const getHistory = () => {
		if (!showHistory) return [];
		try {
			const saved = localStorage.getItem(HISTORY_KEY);
			return saved ? JSON.parse(saved) : [];
		} catch {
			return [];
		}
	};

	const saveToHistory = searchQuery => {
		if (!showHistory || !searchQuery || searchQuery.length < 2) return;
		try {
			const history = getHistory();
			const newHistory = [searchQuery, ...history.filter(q => q !== searchQuery)].slice(
				0,
				HISTORY_LIMIT
			);
			localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
		} catch {
			// ignore
		}
	};

	// Search handler
	const handleSearch = async (searchPage = 1) => {
		if (query.length < 2) {
			addToast({
				title: 'Search',
				description: 'Please enter at least 2 characters',
				color: 'warning'
			});
			return;
		}

		setLoading(true);
		saveToHistory(query);

		try {
			const response = await BaseApi.post('/aircraft/search', {
				query,
				page: searchPage,
				limit: 10
			});

			const responseData = response.data;

			if (responseData.success === false) {
				const errorMsg = responseData.error?.[0] || 'No aircraft found';
				setResults([]);
				setHasMore(false);
				addToast({
					title: 'Search',
					description: errorMsg,
					color: 'warning'
				});
				return;
			}

			const data = Array.isArray(responseData.data) ? responseData.data : [];
			// API returns 11 items if there are more results
			const hasMoreResults = data.length > 10;
			const displayResults = hasMoreResults ? data.slice(0, 10) : data;

			setResults(displayResults);
			setHasMore(hasMoreResults);
			setPage(searchPage);
		} catch (error) {
			console.error('Aircraft search error:', error);
			const errorMsg = error.response?.data?.error?.[0] || 'Failed to search aircraft models';
			addToast({
				title: 'Error',
				description: errorMsg,
				color: 'danger'
			});
			setResults([]);
			setHasMore(false);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSearch(1);
		}
	};

	const handleSelect = model => {
		onSelect(model);
		// Reset state after selection
		setQuery('');
		setResults([]);
		setPage(1);
	};

	const history = getHistory();
	const historyId = React.useId();

	return (
		<div className="space-y-3">
			{/* Search Input */}
			<div className="flex gap-2">
				<Input
					placeholder={placeholder}
					value={query}
					onValueChange={setQuery}
					onKeyDown={handleKeyDown}
					startContent={<Search className="w-4 h-4 text-default-400" />}
					size={size}
					list={showHistory ? historyId : undefined}
				/>
				{showHistory && history.length > 0 && (
					<datalist id={historyId}>
						{history.map((q, index) => (
							<option key={index} value={q} />
						))}
					</datalist>
				)}
				<Button
					color="default"
					size={size}
					onPress={() => handleSearch(1)}
					isLoading={loading}
					isDisabled={query.length < 2}
				>
					Find
				</Button>
			</div>

			{/* Results List */}
			{results.length > 0 && (
				<div className="space-y-1 overflow-y-auto" style={{ maxHeight }}>
					{results.map(result => (
						<Button
							key={result.id}
							variant="light"
							size="sm"
							className="w-full justify-start text-left h-auto py-1.5"
							onPress={() => handleSelect(result)}
						>
							<span className="truncate text-xs">
								{result.manufacturer} {result.model} {result.popularName}
							</span>
						</Button>
					))}

					{hasMore && <p className="text-xs text-default-400 text-center">...and more</p>}

					{/* Pagination */}
					{(page > 1 || hasMore) && (
						<div className="flex justify-between pt-2">
							<Button
								size="sm"
								variant="flat"
								isDisabled={page <= 1}
								onPress={() => handleSearch(page - 1)}
							>
								Previous
							</Button>
							<Button
								size="sm"
								variant="flat"
								isDisabled={!hasMore}
								onPress={() => handleSearch(page + 1)}
							>
								Next
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

/**
 * Display card for a selected aircraft model with option to change.
 *
 * @param {Object} props
 * @param {Object} props.model - Selected model object
 * @param {Function} [props.onChangeClick] - Callback when Change button clicked
 * @param {boolean} [props.showChangeButton=true] - Show Change button
 */
export function AircraftModelCard({ model, onChangeClick, showChangeButton = true }) {
	if (!model) return null;

	return (
		<div className="bg-default-100 rounded-lg p-3">
			<div className="flex items-center justify-between mb-2">
				<h5 className="font-medium text-sm flex items-center gap-2">
					<Plane className="w-4 h-4" />
					{model.manufacturer} {model.model} {model.popularName}
				</h5>
				{showChangeButton && onChangeClick && (
					<Button size="sm" variant="light" onPress={onChangeClick}>
						Change
					</Button>
				)}
			</div>
			<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
				<span className="text-default-500">Type</span>
				<span className="text-right">{model.typeAircraft || '-'}</span>
				<span className="text-default-500">Engine</span>
				<span className="text-right">{model.typeEngine || '-'}</span>
				<span className="text-default-500">Category</span>
				<span className="text-right">{model.aircraftCategory || '-'}</span>
				<span className="text-default-500">Engines</span>
				<span className="text-right">{model.numberEngine || '-'}</span>
				<span className="text-default-500">Seats</span>
				<span className="text-right">{model.numberSeats || '-'}</span>
			</div>
		</div>
	);
}

export default AircraftModelSearch;
